import { useState, useEffect } from 'react';
import { differenceInMonths } from 'date-fns';
import { Client, ClientGoal, ClientActiveSubscription } from '@/types/client';
import { getClientById, updateClient } from '@/lib/apiService';
import { DietaryGoals } from '@/types/dietary-goals';
import { extractErrorMessage, showErrorToast, isMinimumEnergyIntakeError } from '@/utils/toast-utils';

const normalizeSexAtBirthForSave = (value: unknown): 'male' | 'female' | undefined => {
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'male' || normalized === 'female' ? normalized : undefined;
};

const CLIENT_DETAILS_CACHE_TTL_MS = 10_000;
const inFlightClientFetches = new Map<string, Promise<{ client: Client; activeSubscription: ClientActiveSubscription | null }>>();
const clientDetailsCache = new Map<
  string,
  { timestamp: number; client: Client; activeSubscription: ClientActiveSubscription | null }
>();

export const invalidateClientDetailsCache = (clientId?: string) => {
  if (clientId) {
    clientDetailsCache.delete(clientId);
    inFlightClientFetches.delete(clientId);
    return;
  }
  clientDetailsCache.clear();
  inFlightClientFetches.clear();
};

const readCachedClientDetails = (
  clientId: string
): { client: Client; activeSubscription: ClientActiveSubscription | null } | null => {
  const cached = clientDetailsCache.get(clientId);
  if (!cached) return null;
  if (Date.now() - cached.timestamp > CLIENT_DETAILS_CACHE_TTL_MS) {
    clientDetailsCache.delete(clientId);
    return null;
  }
  return { client: cached.client, activeSubscription: cached.activeSubscription };
};

const fetchClientDetails = async (
  clientId: string,
  force = false
): Promise<{ client: Client; activeSubscription: ClientActiveSubscription | null }> => {
  if (!force) {
    const cached = readCachedClientDetails(clientId);
    if (cached) return cached;

    const inFlight = inFlightClientFetches.get(clientId);
    if (inFlight) return inFlight;
  }

  let requestPromise: Promise<{ client: Client; activeSubscription: ClientActiveSubscription | null }>;
  requestPromise = (async () => {
    try {
      const apiClient = await getClientById(clientId, { force });
      const result = {
        client: apiClient.client,
        activeSubscription: apiClient.activeSubscription ?? null,
      };
      clientDetailsCache.set(clientId, { timestamp: Date.now(), ...result });
      return result;
    } finally {
      if (inFlightClientFetches.get(clientId) === requestPromise) {
        inFlightClientFetches.delete(clientId);
      }
    }
  })();

  inFlightClientFetches.set(clientId, requestPromise);
  return requestPromise;
};

interface EditData {
  gender?: string;
  sexAtBirth?: string;
  dateOfBirth?: string;
  location?: string;
  preferredMeasurementSystem?: string;
  heightCm?: number;
  weightKg?: number;
  waistCircumferenceCm?: number;
  hipCircumferenceCm?: number;
  bodyFatPercentage?: number;
  formulaId?: string | number;
  activityLevel?: string;
  pregnancyTrimester?: string | null;
  isTwinPregnancy?: boolean;
  lactationPeriod?: string | null;
  weightLossGoal?: string | null;
  feedingType?: string;
  dailyKcalTarget?: number;
  carbsPercentage?: number;
  proteinPercentage?: number;
  fatPercentage?: number;
  macronutrientsSpread?: number;
  proteinGoalMin?: number;
  proteinGoalMax?: number;
  carbsGoalMin?: number;
  carbsGoalMax?: number;
  fatGoalMin?: number;
  fatGoalMax?: number;
  fiberGoalGrams?: number;
  waterGoalLiters?: number;
}

export const useClientDetails = (clientId: string) => {
  const [client, setClient] = useState<Client | undefined>(undefined);
  const [activeSubscription, setActiveSubscription] = useState<ClientActiveSubscription | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editData, setEditData] = useState<EditData>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSavingChanges, setIsSavingChanges] = useState<boolean>(false);

  // Separate tracking for client details vs dietary goals
  const [hasClientDetailsChanges, setHasClientDetailsChanges] = useState<boolean>(false);
  const [hasDietaryGoalsChanges, setHasDietaryGoalsChanges] = useState<boolean>(false);

  const fetchData = async (isRefresh = false) => {
    if (!clientId) {
      if (!isRefresh) setError('Invalid client ID');
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const result = await fetchClientDetails(clientId, isRefresh);
      setClient(result.client);
      setActiveSubscription(result.activeSubscription);
    } catch (err: any) {
      console.error('Error fetching client data:', err);
      setError(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch client data
  useEffect(() => {
    fetchData();
  }, [clientId]);

  // Function to refresh client data
  const refreshClientData = async () => {
    await fetchData(true);
  };

  const updateLocalClient = (updater: (currentClient: Client) => Client) => {
    setClient((currentClient) => {
      if (!currentClient) return currentClient;
      const nextClient = updater(currentClient);
      clientDetailsCache.set(clientId, {
        timestamp: Date.now(),
        client: nextClient,
        activeSubscription,
      });
      return nextClient;
    });
  };

  // Initialize edit data when client changes or edit mode is entered
  useEffect(() => {
    if (client && isEditMode) {
      // Priority: clientGoal > macrosRanges > hardcoded defaults
      // Note: clientGoal values can be null/undefined (cleared by user), so we check for that explicitly
      setEditData({
        gender: client.gender || '',
        sexAtBirth: client.sexAtBirth ?? '',
        dateOfBirth: client.dateOfBirth,
        location: client.location,
        preferredMeasurementSystem: client.preferredMeasurementSystem || 'metric',
        heightCm: client.heightCm,
        weightKg: client.weightKg,
        activityLevel: client.activityLevel || '',
        pregnancyTrimester: client.pregnancyTrimester || null,
        isTwinPregnancy: client.isTwinPregnancy || false,
        lactationPeriod: client.lactationPeriod || null,
        weightLossGoal: client.lactationGoal || client.weightGoal || null,
        feedingType: client.feedingType || '',
        dailyKcalTarget: client.clientGoal?.eerGoalCalories !== undefined ? client.clientGoal.eerGoalCalories : (client.eerCalories || 2000),
        carbsPercentage: 45,
        proteinPercentage: 25,
        fatPercentage: 30,
        macronutrientsSpread: 100,
        proteinGoalMin: client.clientGoal?.proteinGoalMin !== undefined ? client.clientGoal.proteinGoalMin : (client.proteinGoalMin || client.macrosRanges?.protein?.min || 140),
        proteinGoalMax: client.clientGoal?.proteinGoalMax !== undefined ? client.clientGoal.proteinGoalMax : (client.proteinGoalMax || client.macrosRanges?.protein?.max || 180),
        carbsGoalMin: client.clientGoal?.carbsGoalMin !== undefined ? client.clientGoal.carbsGoalMin : (client.carbsGoalMin || client.macrosRanges?.carbs?.min || 200),
        carbsGoalMax: client.clientGoal?.carbsGoalMax !== undefined ? client.clientGoal.carbsGoalMax : (client.carbsGoalMax || client.macrosRanges?.carbs?.max || 250),
        fatGoalMin: client.clientGoal?.fatGoalMin !== undefined ? client.clientGoal.fatGoalMin : (client.fatGoalMin || client.macrosRanges?.fat?.min || 60),
        fatGoalMax: client.clientGoal?.fatGoalMax !== undefined ? client.clientGoal.fatGoalMax : (client.fatGoalMax || client.macrosRanges?.fat?.max || 80),
        fiberGoalGrams: client.clientGoal?.fiberGoalGrams !== undefined ? client.clientGoal.fiberGoalGrams : (client.fiberGoalGrams || client.macrosRanges?.fiber?.min || 25),
        waterGoalLiters: client.clientGoal?.waterGoalLiters !== undefined ? client.clientGoal.waterGoalLiters : (client.waterGoalLiters || 2.5),
      });
    }
  }, [client, isEditMode]);

  // Track unsaved changes separately for client details and dietary goals
  useEffect(() => {
    if (isEditMode && client) {
      // Calculate age in months to determine if client is an infant (0-12 months)
      const dateOfBirthForAge = editData.dateOfBirth !== undefined ? editData.dateOfBirth : client?.dateOfBirth;
      const ageInMonths = dateOfBirthForAge ? differenceInMonths(new Date(), new Date(dateOfBirthForAge)) : null;
      const isInfant = ageInMonths !== null && ageInMonths >= 0 && ageInMonths < 12;

      // Check client details changes (non-dietary fields)
      const clientDetailsFields = [
        'gender', 
        'sexAtBirth',
        'dateOfBirth', 
        'location', 
        'preferredMeasurementSystem', 
        'heightCm', 
        'weightKg',
        'waistCircumferenceCm',
        'hipCircumferenceCm',
        'bodyFatPercentage',
        'formulaId',
        'activityLevel',
        'pregnancyTrimester',
        'isTwinPregnancy',
        'lactationPeriod',
        'weightLossGoal',
        'feedingType'
      ];
      const hasClientChanges = clientDetailsFields.some(key => {
        // Skip activityLevel for infants (0-12 months)
        if (key === 'activityLevel' && isInfant) {
          return false;
        }
        
        // Only check feedingType for infants (0-12 months)
        if (key === 'feedingType' && !isInfant) {
          return false;
        }

        const editValue = editData[key as keyof typeof editData];
        // Map editData field names to client field names where they differ
        const clientKey = key === 'weightLossGoal' ? 'lactationGoal' : key;
        const clientValue = client[clientKey as keyof Client];
        // Handle undefined/null comparison - treat undefined and null as the same
        if (editValue === undefined && (clientValue === undefined || clientValue === null)) {
          return false;
        }
        if (editValue === null && (clientValue === undefined || clientValue === null)) {
          return false;
        }
        return editValue !== clientValue;
      });
      setHasClientDetailsChanges(hasClientChanges);

      // Check dietary goals changes - compare with clientGoal if it exists, otherwise with fallback values
      const clientGoal = client.clientGoal as ClientGoal | null;
      const dietaryGoalsFields = [
        'dailyKcalTarget', 'proteinGoalMin', 'proteinGoalMax', 'carbsGoalMin', 
        'carbsGoalMax', 'fatGoalMin', 'fatGoalMax', 'fiberGoalGrams', 'waterGoalLiters', 'cuisineType', 'allergies'
      ];
      const hasDietaryChanges = dietaryGoalsFields.some(key => {
        const editValue = editData[key as keyof typeof editData];
        let clientValue: any;
        
        if (key === 'dailyKcalTarget') {
          clientValue = client.clientGoal?.eerGoalCalories !== undefined ? client.clientGoal.eerGoalCalories : client.eerCalories;
        } else if (key === 'cuisineType') {
          clientValue = client.clientGoal?.cuisineTypes || '';
        } else if (key === 'allergies') {
          clientValue = client.clientGoal?.allergies || '';
        } else {
          // Priority: clientGoal > macrosRanges
          // Note: clientGoal values can be null/undefined (cleared by user), so we check for that explicitly
          const fieldName = key.replace('Goal', '') as keyof Client;
          const clientGoalValue = client.clientGoal?.[key as keyof ClientGoal];
          clientValue = clientGoalValue !== undefined ? clientGoalValue : 
                       (client[key as keyof Client] || 
                       (key.includes('Min') ? client.macrosRanges?.[fieldName.replace('Min', '') as keyof typeof client.macrosRanges]?.min : 
                        key.includes('Max') ? client.macrosRanges?.[fieldName.replace('Max', '') as keyof typeof client.macrosRanges]?.max : 
                        client.macrosRanges?.[fieldName as keyof typeof client.macrosRanges]?.min));
        }
        
        return editValue !== clientValue;
      });
      setHasDietaryGoalsChanges(hasDietaryChanges);

      // Overall changes
      setHasUnsavedChanges(hasClientChanges || hasDietaryChanges);
    }
  }, [editData, client, isEditMode]);


  const enterEditMode = () => setIsEditMode(true);

  const exitEditMode = (forceExit: boolean = false) => {
    if (!forceExit && hasUnsavedChanges) {
      const confirmExit = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmExit) return;
    }
    setIsEditMode(false);
    setEditData({});
    setHasUnsavedChanges(false);
    setHasClientDetailsChanges(false);
    setHasDietaryGoalsChanges(false);
  };

  // Function to reset dietary goals to original values (called by DietaryIntakeGoals component)
  const resetDietaryGoals = () => {
    // Reset dietary goals changes without API call
    setHasDietaryGoalsChanges(false);
    // The DietaryIntakeGoals component will handle resetting its own state
  };

  // Function to mark dietary goals as saved (called after successful save)
  const markDietaryGoalsAsSaved = () => {
    setHasDietaryGoalsChanges(false);
    setHasUnsavedChanges(false);
  };

  // Function to reset all change tracking states (called after successful save of all changes)
  const resetAllChangeTracking = () => {
    setHasUnsavedChanges(false);
    setHasClientDetailsChanges(false);
    setHasDietaryGoalsChanges(false);
  };

  // Function to set saving state (for external use)
  const setSavingState = (saving: boolean) => {
    setIsSavingChanges(saving);
  };

  // Validation function for lactating clients
  const validateLactatingClient = (): { isValid: boolean; errorMessage?: string } => {
    // Get current values from editData or fallback to client data
    const gender = editData.gender !== undefined ? editData.gender : client?.gender;
    const sexAtBirth = editData.sexAtBirth !== undefined ? editData.sexAtBirth : client?.sexAtBirth;
    const dateOfBirth = editData.dateOfBirth !== undefined ? editData.dateOfBirth : client?.dateOfBirth;
    const lactationPeriod = editData.lactationPeriod !== undefined ? editData.lactationPeriod : client?.lactationPeriod;
    const isTwinPregnancy = editData.isTwinPregnancy !== undefined ? editData.isTwinPregnancy : client?.isTwinPregnancy;
    const weightLossGoal = editData.weightLossGoal !== undefined ? editData.weightLossGoal : (client?.lactationGoal || client?.weightGoal);
    const effectiveSexForValidation = String(sexAtBirth || gender || '').trim().toLowerCase();

    // Only validate for female clients
    if (effectiveSexForValidation !== 'female') {
      return { isValid: true };
    }

    // Calculate age and skip validation for females under 18
    if (dateOfBirth) {
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      let calculatedAge = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        calculatedAge--;
      }
      
      if (calculatedAge < 18) {
        // Skip pregnancy/lactation validation for females under 18
        return { isValid: true };
      }
    }

    // Check if client is lactating
    const isLactating = lactationPeriod && lactationPeriod !== 'none' && lactationPeriod !== null;

    if (isLactating) {
      const missingFields: string[] = [];

      // Validate pregnancy type (isTwinPregnancy) - must be set (not undefined)
      if (isTwinPregnancy === undefined) {
        missingFields.push('Pregnancy Type');
      }

      // Validate weight loss goal - must be set
      if (!weightLossGoal || weightLossGoal === null || weightLossGoal === '') {
        missingFields.push('Weight Loss Goal');
      }

      if (missingFields.length > 0) {
        return {
          isValid: false,
          errorMessage: `Cannot save: The following mandatory fields are required for lactating clients: ${missingFields.join(', ')}. Please complete all required fields before saving.`
        };
      }
    }

    return { isValid: true };
  };

  const saveAllChanges = async () => {
    if (!client || isSavingChanges) return;

    // Validate lactating client requirements before saving
    const validation = validateLactatingClient();
    if (!validation.isValid) {
      setIsSavingChanges(false);
      showErrorToast(validation.errorMessage || 'Validation failed', 'Cannot Save Client');
      return;
    }

    setIsSavingChanges(true);
    try {
      // Calculate age in months to determine if client is an infant (0-12 months)
      const dateOfBirthForAge = editData.dateOfBirth !== undefined ? editData.dateOfBirth : client?.dateOfBirth;
      const ageInMonths = dateOfBirthForAge ? differenceInMonths(new Date(), new Date(dateOfBirthForAge)) : null;
      const isInfant = ageInMonths !== null && ageInMonths >= 0 && ageInMonths < 12;

      // Prepare client details data (non-dietary fields)
      const clientDetailsPayload: any = {};
      const clientDetailsFields = [
        'gender', 
        'sexAtBirth',
        'dateOfBirth', 
        'location', 
        'preferredMeasurementSystem', 
        'heightCm', 
        'weightKg',
        'waistCircumferenceCm',
        'hipCircumferenceCm',
        'bodyFatPercentage',
        'formulaId',
        'activityLevel',
        'pregnancyTrimester',
        'isTwinPregnancy',
        'lactationPeriod',
        'weightLossGoal',
        'feedingType'
      ];
      
      clientDetailsFields.forEach(key => {
        // Skip activityLevel for infants (0-12 months)
        if (key === 'activityLevel' && isInfant) {
          return;
        }
        
        // Only include feedingType for infants (0-12 months)
        if (key === 'feedingType' && !isInfant) {
          return;
        }
        
        const value = editData[key as keyof typeof editData];
        // For boolean fields like isTwinPregnancy, include the value even if false
        if (key === 'isTwinPregnancy') {
          if (value !== undefined) {
            clientDetailsPayload[key] = value;
          }
        } else if (key === 'formulaId') {
          // Map formulaId to bmrFormulaId (backend expects bmrFormulaId)
          if (value !== undefined && value !== null && value !== '') {
            clientDetailsPayload['bmrFormulaId'] = String(value);
          }
        } else if (key === 'weightLossGoal') {
          // Map weightLossGoal based on lactation status
          // If lactating, map to 'lactationGoal', otherwise map to 'weightGoal'
          const isLactating = editData.lactationPeriod && editData.lactationPeriod !== 'none';
          
          if (value !== undefined && value !== null && value !== '') {
            if (isLactating) {
              clientDetailsPayload['lactationGoal'] = value;
              // Clear weightGoal if switching to lactation
              clientDetailsPayload['weightGoal'] = null;
            } else {
              clientDetailsPayload['weightGoal'] = value;
              // Clear lactationGoal if switching away from lactation
              clientDetailsPayload['lactationGoal'] = null;
            }
          } else {
            // If value is cleared, clear both
            clientDetailsPayload['lactationGoal'] = null;
            clientDetailsPayload['weightGoal'] = null;
          }
        } else if (key === 'sexAtBirth') {
          const normalizedSexAtBirth = normalizeSexAtBirthForSave(value);
          if (normalizedSexAtBirth) {
            clientDetailsPayload[key] = normalizedSexAtBirth;
          }
        } else if (value !== undefined && value !== null && value !== '') {
          // For other fields, allow 0 as a valid value, but exclude undefined/null/empty string
          clientDetailsPayload[key] = value;
        }
      });

    
      // Update client details if there are changes
      if (hasClientDetailsChanges && Object.keys(clientDetailsPayload).length > 0) {
        try {
          // First attempt: try without override parameter
          await updateClient(client.id, clientDetailsPayload);
          
          // Refresh client data to get updated values from API
          await refreshClientData();
        } catch (error: any) {
          // Check if error is related to minimum energy intake
          if (isMinimumEnergyIntakeError(error)) {
            // Show warning and retry with override parameter
            const confirmed = window.confirm(
              'Warning: The calculated calories are below the minimum energy intake requirement. Do you want to override this restriction and proceed?'
            );
            
            if (!confirmed) {
              showErrorToast(error, "Update cancelled");
              return;
            }
            
            // Retry with override parameter
            try {
              await updateClient(client.id, {
                ...clientDetailsPayload,
                overrideMinimumEnergyIntake: true
              });
              
              // Refresh client data to get updated values from API
              await refreshClientData();
            } catch (retryError: any) {
              console.error('Client update retry error:', retryError);
              showErrorToast(retryError, "Update failed");
              return;
            }
          } else {
            // For other errors, show error toast and rethrow
            throw error;
          }
        }
      }

      // Note: Dietary goals will be handled separately by the DietaryIntakeGoals component
      // Only reset the change tracking for client details that were actually saved
      setHasClientDetailsChanges(false);
      
      // Update overall unsaved changes flag based on remaining changes
      setHasUnsavedChanges(hasDietaryGoalsChanges);

    
    } catch (error) {
      console.error('Error updating client:', error);
      showErrorToast(error, "Update failed");
    } finally {
      console.log('Save operation completed, setting isSavingChanges to false');
      setIsSavingChanges(false);
    }
  };

  const updateEditData = (field: string, value: any) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  return {
    client,
    activeSubscription,
    isLoading,
    error,
    isEditMode,
    editData,
    hasUnsavedChanges,
    hasClientDetailsChanges,
    hasDietaryGoalsChanges,
    isSavingChanges,
    enterEditMode,
    exitEditMode,
    saveAllChanges,
    updateEditData,
    refreshClientData,
    updateLocalClient,
    resetDietaryGoals,
    markDietaryGoalsAsSaved,
    resetAllChangeTracking,
    setSavingState,
  };
};
